/**
 * Browser RPC allowlist.
 *
 * The device relay is a byte pipe to the engine's *entire* control surface:
 * once a socket is authenticated, nothing between the browser and the engine
 * inspects the method name. A WorkOS login therefore currently grants a web
 * page the same power as the native app — terminals, filesystem writes, git
 * mutations, harness installs, self-update, and account operations.
 *
 * This module is the browser's own policy layer, and it is enforced in the one
 * place every outbound call passes through (`ZeronSession`/`AppState`), so the
 * UI cannot accidentally reach a method that is not listed here.
 *
 * What this layer is and is not:
 *
 * - It **is** a defense against a compromised or buggy web front-end reaching
 *   dangerous engine surface — XSS in a transcript, a malicious paste, a
 *   dependency that phones home. It bounds the blast radius of the *page*.
 * - It is **not** a server-side authorization boundary. The engine still trusts
 *   the authenticated user completely; anyone who can obtain an access token
 *   and dial the relay directly (curl, a rogue native client) bypasses this
 *   list entirely. Real enforcement needs either a per-method gate inside the
 *   engine keyed on a session capability, or a BFF that proxies RPC and filters
 *   methods. Both are engine/edge changes, documented in README.md as remaining
 *   work. Until then, treat the web app as one more native client of a
 *   single-user workspace, not as a sandboxed one.
 *
 * Consequence for the deployment: the app must be reachable only by its owner
 * (it is a single-user control panel behind WorkOS), and its origin must not
 * serve untrusted third-party script.
 */

export type DenyCode = "not_allowlisted" | "bad_params";

export type AllowDecision =
  | { allowed: true }
  | { allowed: false; reason: string; code: DenyCode };

/** Read + prompt + queue + respond-input. Everything the daily loop needs. */
export const ALLOWED_METHODS: readonly string[] = [
  // identity / readiness
  "EngineInfo",
  "EngineReady",
  "LocalDevice",
  // registry watches (sidebar, device picker, session dots)
  "WatchChats",
  "WatchDevices",
  "WatchSpaces",
  "WatchSessions",
  "WatchSidebarPreferences",
  "WatchConnectivity",
  // transcript
  "WatchDocMessages",
  "SearchChats",
  "FocusChat",
  // composer + queue
  "QueueCommand",
  "QueueMessage",
  "WatchQueue",
  "UpdateQueuedMessage",
  "MoveQueuedMessage",
  "RemoveQueuedMessage",
  "SendQueuedMessageNow",
  "SteerQueuedMessageNow",
  "BeginQueuedMessageEdit",
  "RenewQueuedMessageEdit",
  "FinishQueuedMessageEdit",
  // harness/model catalog (needed to render the composer's pickers)
  "ListHarnesses",
  "ListModels",
  // read-only git inspection, so the new-chat screen can offer a project, a
  // base ref and an isolated worktree. None of these mutate the repository:
  // the mutating git surface (`SwitchRef`, `Pull`, `DiscardWorkingTree`,
  // `AddRepo`, ...) stays denied, as does `CreateWorktree` — a worktree is
  // created by the run itself, which is a command the user already sends.
  "ListRefs",
  "ListBranches",
  "ListWorktrees",
  // Browsing the repository's change requests. Read-only by construction: the
  // list runs `gh`/`fj` with a list subcommand on the checkout's own host, and
  // the reply is a summary. `ChangeRequestAction` — the merge/close/auto-merge
  // surface — stays denied, because a leaked web token must not be able to
  // merge anything.
  "ListChangeRequests",
  "GetChangeRequestDetail",
  // sidebar hygiene: rename / archive / mark seen on chats the user owns
  "Mutate"
] as const;

/**
 * Methods that exist and are deliberately *not* reachable from the browser.
 * Kept as data (not just prose) so the README, the tests, and any future
 * server-side gate agree on the same list. Grouped by why they are excluded.
 */
export const DENIED_METHODS: Readonly<Record<string, readonly string[]>> = {
  "filesystem write": [
    "WriteWorkspaceFile",
    "MoveWorkspaceEntry",
    "DeleteWorkspaceEntry",
    "CreateWorktree",
    "DeleteWorktree",
    "SetWorktreeSettings",
    "CleanupWorktrees",
    "UploadChunk",
    "UploadCommit",
    "UpsertProjectAction",
    "DeleteProjectAction",
    "RunProjectAction"
  ],
  "filesystem read (kept out for now)": [
    "ListFolders",
    "ListDrives",
    "SearchFiles",
    "ListWorkspaceDirectory",
    "SearchWorkspaceFiles",
    "ReadWorkspaceFile",
    "ReadWorkspaceImage",
    "WatchWorkspaceFiles",
    "ReadAttachmentChunk",
    "FetchToolBlob",
    "ReadToolDetails"
  ],
  terminals: [
    "OpenTerminal",
    "SubscribeTerminal",
    "WriteTerminal",
    "ResizeTerminal",
    "CloseTerminal"
  ],
  "git mutation": [
    "AddRepo",
    "CloneRepo",
    "CreateRepo",
    "FetchAll",
    "Pull",
    "SwitchRef",
    "DiscardWorkingTree"
  ],
  "updates and installs": [
    "InstallHarness",
    "CancelInstall",
    "UpdateStatus",
    "ApplyUpdate",
    "WatchHarnessUpdates",
    "CheckHarnessUpdates",
    "ApplyHarnessUpdate",
    "CancelHarnessUpdate",
    "DismissHarnessUpdate",
    "SetHarnessUpdatePolicy",
    "ImportLocalWorkspace",
    "ImportChatHistory"
  ],
  "account and org operations": [
    "AuthStatus",
    "SignIn",
    "SignInHeadless",
    "CompleteSignIn",
    "SignOut",
    "ListOrgs",
    "CreateOrg",
    "SelectOrg",
    "ListAgentAccounts",
    "ActivateAgentAccount",
    "ForgetAgentAccount",
    "StartAgentLogin",
    "CompleteAgentLogin",
    "PollAgentLogin",
    "CancelAgentLogin",
    "SetBifrostLimits"
  ],
  "process and engine lifecycle": ["StopEngine", "ProbeSync", "RetryDelivery", "SyncStatus"],
  // The mutating half of the change-request surface. Browsing is allowed;
  // merging, closing and toggling auto-merge are not. This is the whole point of
  // splitting the read from the action in the engine.
  "change-request mutation": ["ChangeRequestAction"],
  "arbitrary integration actions": ["Integration", "WatchPreviews"],
  "unvetted queue edits": ["TakeProjectActionSetup", "RelayCommand", "ForkSideChat"]
};

const ALLOWED_SET: ReadonlySet<string> = new Set(ALLOWED_METHODS);
const DENIED_SET: ReadonlySet<string> = new Set(Object.values(DENIED_METHODS).flat());

/**
 * `Mutate` is one RPC name for many workspace-doc operations, so the name alone
 * is not enough: `deleteChat` is a chat the user owns and expects to be able to
 * remove, while `createSpace`/`renameSpace`/`deleteSpace` and `renameDevice`
 * rewrite workspace structure the web app has no UI for. Only the chat-scoped
 * ops are allowed, and each must name a chat.
 */
export const ALLOWED_MUTATE_OPS: readonly string[] = [
  // Starting a new chat is the web client's primary action; without it the only
  // way to begin one was the desktop app.
  "createChat",
  "renameChat",
  "setChatArchived",
  "deleteChat",
  "markChatSeen",
  "changeSidebarPin"
] as const;

const ALLOWED_MUTATE_SET: ReadonlySet<string> = new Set(ALLOWED_MUTATE_OPS);

/** Params that can redirect a call at another device — allowed, because the
 * device picker is part of the daily loop, but recorded so a future
 * server-side gate can see exactly which methods accept it. */
export const TARGETABLE_METHODS: readonly string[] = [
  "EngineInfo",
  "ListHarnesses",
  "ListModels",
  "QueueCommand",
  "WatchDocMessages",
  "WatchQueue",
  "QueueMessage",
  "UpdateQueuedMessage",
  "MoveQueuedMessage",
  "RemoveQueuedMessage",
  "SendQueuedMessageNow",
  "SteerQueuedMessageNow",
  "BeginQueuedMessageEdit",
  "RenewQueuedMessageEdit",
  "FinishQueuedMessageEdit"
] as const;

const TARGETABLE_SET: ReadonlySet<string> = new Set(TARGETABLE_METHODS);

/** Methods whose reply is a stream. The UI must subscribe to these (and
 * re-subscribe after every reconnect) instead of calling them unary. */
export const STREAM_METHODS: readonly string[] = [
  "WatchChats",
  "WatchDevices",
  "WatchSpaces",
  "WatchSessions",
  "WatchSidebarPreferences",
  "WatchConnectivity",
  "WatchDocMessages",
  "WatchQueue"
] as const;

const STREAM_SET: ReadonlySet<string> = new Set(STREAM_METHODS);

/**
 * The subset of streams the engine will forward to another device when
 * `targetDeviceId` is present (`is_stream_method` in `crates/engine/src/rpc.rs`).
 * Sending `targetDeviceId` on a stream outside this set makes the engine reply
 * with a unary error instead of items.
 */
export const FORWARDABLE_STREAM_METHODS: readonly string[] = [
  "WatchDocMessages",
  "WatchQueue"
] as const;

const FORWARDABLE_STREAM_SET: ReadonlySet<string> = new Set(FORWARDABLE_STREAM_METHODS);

export const isStreamMethod = (method: string): boolean => STREAM_SET.has(method);

export const isForwardableStream = (method: string): boolean => FORWARDABLE_STREAM_SET.has(method);

/** True when `targetDeviceId` on this method is understood by the engine. */
export const acceptsTargetDevice = (method: string): boolean => TARGETABLE_SET.has(method);

/** Every method the browser is allowed to name, for docs and tests. */
export const allowedMethodCount = (): number => ALLOWED_SET.size;

export const isDeniedMethod = (method: string): boolean => DENIED_SET.has(method);

/**
 * The single gate. Called for every outbound RPC before it is framed.
 *
 * Unknown methods are rejected — an allowlist that passes anything it has not
 * heard of is not an allowlist. When a future engine adds a method the browser
 * needs, it must be added to [`ALLOWED_METHODS`] explicitly.
 */
export const checkMethod = (method: string, params: unknown = {}): AllowDecision => {
  if (!ALLOWED_SET.has(method)) {
    return {
      allowed: false,
      code: "not_allowlisted",
      reason: isDeniedMethod(method)
        ? `${method} is deliberately not exposed to the browser client`
        : `${method} is not on the browser allowlist`
    };
  }
  const record = (typeof params === "object" && params !== null ? params : {}) as Record<
    string,
    unknown
  >;
  if (method === "Mutate") {
    const op = record.op;
    if (typeof op !== "string" || !ALLOWED_MUTATE_SET.has(op)) {
      return {
        allowed: false,
        code: "bad_params",
        reason: `Mutate op ${JSON.stringify(op)} is not exposed to the browser client`
      };
    }
    if (typeof record.chatId !== "string" || record.chatId.length === 0) {
      return { allowed: false, code: "bad_params", reason: `Mutate ${op} requires a chatId` };
    }
    if (op === "createChat") {
      // The engine refuses a chat with no host: it needs a device to run on.
      const hasSpace = typeof record.spaceId === "string" && record.spaceId.length > 0;
      const hasDevice = typeof record.deviceId === "string" && record.deviceId.length > 0;
      if (!hasSpace && !hasDevice) {
        return {
          allowed: false,
          code: "bad_params",
          reason: "createChat needs a spaceId or a deviceId"
        };
      }
    }
  }
  if (method === "WatchDocMessages" || method === "QueueMessage" || method === "WatchQueue") {
    if (typeof record.chatId !== "string" || record.chatId.length === 0) {
      return { allowed: false, code: "bad_params", reason: `${method} requires a chatId` };
    }
  }
  if (method === "QueueCommand") {
    if (typeof record.chatId !== "string" || record.chatId.length === 0) {
      return { allowed: false, code: "bad_params", reason: "QueueCommand requires a chatId" };
    }
    const command = record.command;
    const kind = (command as { kind?: unknown } | undefined)?.kind;
    if (kind !== "run" && kind !== "steer" && kind !== "interrupt" && kind !== "respondInput") {
      return {
        allowed: false,
        code: "bad_params",
        reason: `QueueCommand kind ${JSON.stringify(kind)} is not a session command`
      };
    }
  }
  if (method === "QueueMessage" && typeof record.text !== "string") {
    return { allowed: false, code: "bad_params", reason: "QueueMessage requires text" };
  }
  if (record.targetDeviceId !== undefined && !TARGETABLE_SET.has(method)) {
    return {
      allowed: false,
      code: "bad_params",
      reason: `${method} does not accept targetDeviceId (the engine will not forward it)`
    };
  }
  if (
    record.targetDeviceId !== undefined &&
    STREAM_SET.has(method) &&
    !FORWARDABLE_STREAM_SET.has(method)
  ) {
    return {
      allowed: false,
      code: "bad_params",
      reason: `${method} is a local-only watch; targetDeviceId would not be forwarded`
    };
  }
  return { allowed: true };
};

export class RpcDeniedError extends Error {
  constructor(
    readonly method: string,
    message: string,
    readonly code: DenyCode
  ) {
    super(message);
    this.name = "RpcDeniedError";
  }
}
